import Container, { ContainerProps } from '@mui/material/Container';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { AppContext } from '../../contexts/app-context';
import { AuthContext } from '../../contexts/auth-context';
import { CartContext } from '../../contexts/cart-context';
import { useRetryFailedQueries } from '../../hooks/use-retry-failed-queries';
import { useSessionActions } from '../../hooks/use-session-actions';
import { useAgentAccessDenied } from '../alliance/agent-access';
import { LoadFailedScreen, NoAgentScreen } from '../alliance/system-screens';
import { AnimateInProps } from '../animate-in';
import { RouteErrorBoundary } from './route-error-boundary';
import { RouteWrapper } from './route-wrapper';

/**
 * Wraps every route body. The App Bar now lives once in the shell
 * (components/dashboard.tsx); this component tells the shell the page title
 * and loading state, sets the document title, and renders the page content,
 * the load error or the error boundary.
 *
 * While `loading`, the content area stays empty and the shell shows the 3px
 * progress bar under the App Bar (after 150ms). `hasError` shows "Couldn't
 * load this page" with Try again (refetches the failed queries). While
 * impersonating, if every request for that agent was refused (403), the page
 * shows "You don't have access to this agent's data" with Stop impersonating.
 */
export function RouteContainer(props: {
  children: JSX.Element;
  routeTitle: string;
  withContainer?: boolean;
  maxWidth?: ContainerProps['maxWidth'];
  loading?: boolean;
  hasError?: boolean;
  paddingBottom?: number;
  nofade?: AnimateInProps['nofade'];
  notransform?: AnimateInProps['notransform'];
  noanimation?: boolean;
}) {
  // Context
  const { dispatch } = React.useContext(AppContext);
  const cartContext = React.useContext(CartContext);
  const { impersonatedAgent } = React.useContext(AuthContext).state;
  // Hooks
  const retryFailed = useRetryFailedQueries();
  const { stopImpersonating } = useSessionActions();
  const agentDenied = useAgentAccessDenied(impersonatedAgent?.AgtNo);

  const cartDispatch = cartContext.dispatch;
  const loading = !!props.loading;

  React.useEffect(() => {
    // If the drawer is open when this component mounts, close it
    dispatch({ type: 'DRAWER_CLOSE' });
    cartDispatch({ type: 'VIEW_CART', payload: false });
  }, [dispatch, cartDispatch]);

  React.useEffect(() => {
    // Show this page's title and loading state in the shell's App Bar
    dispatch({
      type: 'SET_ROUTE',
      payload: { title: props.routeTitle, loading },
    });
  }, [dispatch, props.routeTitle, loading]);

  React.useEffect(() => {
    // Don't leave the loading bar running after leaving the page
    return () =>
      dispatch({ type: 'SET_ROUTE', payload: { title: '', loading: false } });
  }, [dispatch]);

  let content = props.children;
  if (props.withContainer) {
    content = <Container maxWidth={props.maxWidth}>{props.children}</Container>;
  }

  let routeContent: JSX.Element | null = null;
  if (impersonatedAgent && agentDenied) {
    // Every request for the impersonated agent came back 403
    routeContent = (
      <NoAgentScreen
        agent={{
          name:
            [impersonatedAgent.Fname, impersonatedAgent.Lname]
              .filter(Boolean)
              .join(' ') ||
            impersonatedAgent.AgtName ||
            '',
          id: impersonatedAgent.AgtNo || '',
        }}
        onStopImpersonating={stopImpersonating}
      />
    );
  } else if (loading) {
    routeContent = null;
  } else if (props.hasError) {
    // The page's data didn't load: offline vs. server trouble
    routeContent = <LoadFailedScreen layout="inline" onRetry={retryFailed} />;
  } else {
    routeContent = (
      <RouteErrorBoundary>
        <RouteWrapper
          paddingBottom={props.paddingBottom}
          nofade={props.nofade}
          notransform={props.notransform}
          noanimation={props.noanimation}
        >
          {content}
        </RouteWrapper>
      </RouteErrorBoundary>
    );
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>Leads · {props.routeTitle}</title>
      </Helmet>

      {routeContent}
    </React.Fragment>
  );
}
